import type { JugadorEnMesa, MesaPublica } from '../datos/index.js'
import { Bandera } from './Bandera.js'

/**
 * Una mesa contada como un enfrentamiento: dos bandos y el marcador en el medio.
 *
 * Es la tarjeta de la vitrina, la que se pasa de costado para ver cómo van las
 * mesas sin entrar a ninguna. Al revés que `MesaEnJuego`, que es la ficha de la
 * lista completa y tiene que decir todo (quién no se presentó, cuántos puntos
 * hizo cada lado), esta dice una sola cosa y la dice grande: quién le ganó a
 * quién y por cuánto. Lo demás está a un toque, en la pestaña de mesas.
 *
 * Los nombres van cortos («Marta Q.») porque el ancho es el de media tarjeta y
 * en un teléfono son cuatro nombres en una fila. El completo queda en el
 * `title`, y en la pestaña de mesas.
 */

/** «Marta Quiroga Ibáñez» → «Marta Q.». Un nombre de una palabra queda igual. */
export function nombreCorto(nombre: string): string {
  const partes = nombre.trim().split(/\s+/)
  const [primero, segundo] = partes
  if (!primero) return nombre
  return segundo ? `${primero} ${segundo[0]?.toUpperCase()}.` : primero
}

/** Las iniciales del círculo: la del nombre y la del primer apellido. */
export function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || '?'
}

/* Los círculos hacen de escudo del bando, como el logo de un equipo en una
   transmisión: se reconoce el lado antes de leer los nombres. */
function Escudo({
  jugadores,
  estado,
}: {
  jugadores: JugadorEnMesa[]
  estado: 'gana' | 'pierde' | 'sin-cargar'
}) {
  return (
    <span className={`vs-escudo ${estado}`} aria-hidden="true">
      {jugadores.map((j) => (
        <span key={j.participanteId} className="vs-avatar">
          {iniciales(j.nombre)}
        </span>
      ))}
    </span>
  )
}

function Nombres({
  jugadores,
  estado,
  alineado,
}: {
  jugadores: JugadorEnMesa[]
  estado: 'gana' | 'pierde' | 'sin-cargar'
  alineado: 'inicio' | 'fin'
}) {
  return (
    <ul className={`vs-nombres-lado ${estado} ${alineado}`}>
      {jugadores.map((j) => (
        <li key={j.participanteId} title={j.nombre}>
          <Bandera codigo={j.pais} />
          <span className="acortar">{nombreCorto(j.nombre)}</span>
        </li>
      ))}
    </ul>
  )
}

export function MesaVs({ mesa, alTocar }: { mesa: MesaPublica; alTocar?: () => void }) {
  const marcador = mesa.marcador
  const bando = (n: 1 | 2) => mesa.jugadores.filter((j) => j.bando === n)
  const estadoDe = (n: 1 | 2): 'gana' | 'pierde' | 'sin-cargar' => {
    if (!marcador || marcador.ganador === null) return 'sin-cargar'
    return marcador.ganador === n ? 'gana' : 'pierde'
  }

  const contenido = (
    <>
      <header className="vs-cabecera">
        <span className="vs-mesa">
          Mesa <b>{mesa.numero}</b>
        </span>
        {marcador ? (
          <span className="pastilla cerrado">Terminada</span>
        ) : (
          <span className="pastilla en-vivo">
            <span className="punto-vivo" aria-hidden="true" />
            Jugando
          </span>
        )}
      </header>

      {/* Dos renglones y no tres columnas: arriba los escudos con el marcador en
          el medio, abajo los nombres con la mitad del ancho cada bando. En tres
          columnas el «141 : 200» se come el lugar y los nombres quedan en tres
          letras. */}
      <div className="vs-cuerpo">
        <Escudo jugadores={bando(1)} estado={estadoDe(1)} />
        <div className="vs-centro">
          <span className="vs-marca" aria-hidden="true">
            V<span>/</span>S
          </span>
          {marcador ? (
            <span
              className="vs-cifras"
              aria-label={`${marcador.bando1} a ${marcador.bando2}`}
            >
              <b className={estadoDe(1)}>{marcador.bando1}</b>
              <span aria-hidden="true">:</span>
              <b className={estadoDe(2)}>{marcador.bando2}</b>
            </span>
          ) : (
            <span className="vs-objetivo">a {mesa.objetivoPuntos}</span>
          )}
        </div>
        <Escudo jugadores={bando(2)} estado={estadoDe(2)} />
      </div>

      <div className="vs-nombres">
        <Nombres jugadores={bando(1)} estado={estadoDe(1)} alineado="inicio" />
        <Nombres jugadores={bando(2)} estado={estadoDe(2)} alineado="fin" />
      </div>
    </>
  )

  return alTocar ? (
    <button type="button" className="vs" onClick={alTocar}>
      {contenido}
    </button>
  ) : (
    <article className="vs">{contenido}</article>
  )
}
